import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from "@mui/material";
import { 
  ChevronLeft, 
  ChevronRight, 
  DragIndicator,
  Reorder 
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DriveEtaIcon from "@mui/icons-material/DriveEta";

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
};

const defaultData = {
  firstName: "Matthew",
  lastName: "Eliot",
  currentPosition: "Senior Web Developer",
  phone: "+1 (970) 333-3833",
  email: "matthew.eliot@mail.com",
  city: "New York, NY",
  website: "https://linkedin.com/mattheweliot",
  linkedIn: "",
  drivingLicence: "",
  profile: "Senior Web Developer specializing in front end development. Experienced with all stages of the development cycle for dynamic web projects. Well-versed in numerous programming languages including HTML5, PHP OOP, JavaScript, CSS, MySQL. Strong background in project management and customer relations.",
  skills: [
    { name: "HTML5", rating: 95 },
    { name: "CSS3", rating: 90 },
    { name: "JavaScript", rating: 85 },
    { name: "React", rating: 80 },
    { name: "Node.js", rating: 75 },
    { name: "SQL", rating: 70 },
  ],
  education: [
    {
      degree: "B.S. in Computer Science",
      fieldOfStudy: "Computer Science",
      schoolName: "New York University",
      schoolLocation: "New York, NY",
      gradMonth: "May",
      gradYear: "2015"
    }
  ],
  workExperiences: [
    {
      jobTitle: "Senior Web Developer",
      employer: "Tech Solutions Inc.",
      location: "New York, NY",
      startMonth: "Jan", startYear: "2018",
      endMonth: "Dec",   endYear: "2023",
      current: false,
      description: "• Led a team of 5 developers on a major e-commerce platform redesign.\n• Implemented new features and optimized existing code, reducing page load times by 30%."
    },
    {
      jobTitle: "Web Developer",
      employer: "Digital Innovations Co.",
      location: "New York, NY",
      startMonth: "Jun", startYear: "2015",
      endMonth: "Dec",   endYear: "2017",
      current: false,
      description: "• Developed and maintained client websites using HTML, CSS, and JavaScript.\n• Collaborated with designers to create user-friendly interfaces."
    }
  ],
  certifications: [
    { name: "Certified Web Professional", provider: "W3Schools", year: "2019" }
  ]
};

// ✅ FIX: px-based page sizes for strict screen containment
const pageSizeMap = {
  A4:        { width: "794px",  minHeight: "1123px" },
  Letter:    { width: "816px",  minHeight: "1056px" },
  Legal:     { width: "816px",  minHeight: "1344px" },
  A3:        { width: "1123px", minHeight: "1587px" },
  Executive: { width: "696px",  minHeight: "1008px" },
};

const defaultSectionOrder = [
  'summary', 'skills', 'experience', 'education', 'certifications',
  'languages', 'accomplishments', 'additionalInfo', 'softwareSkills',
  'volunteering', 'interests', 'websites', 'references'
];

// ✅ Common safe text style
const safeText = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  minWidth: 0,
};

const ResumeMatthew = ({
  theme = "#1a2b47",
  color = "#1a2b47",
  font = "Arial",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "20px",
  sectionSpacing = 30,
  paragraphSpacing = 12,
  lineSpacing = 22,
  topBottomMargin = 30,
  sideMargins = 30,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  exporting = false,
  enableDragDrop = true,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const data = { ...defaultData, ...formData };
  const [themeColor, setThemeColor] = useState(color || theme);

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages]             = useState([]);
  const resumeRef = useRef(null);

  const [savedSummary, setSavedSummary]               = useState(data.profile);
  const [savedSkills, setSavedSkills]                 = useState(data.skills);
  const [educationEntries, setEducationEntries]       = useState(data.education);
  const [workExperiences, setWorkExperiences]         = useState(data.workExperiences);
  const [savedCertifications, setSavedCertifications] = useState(data.certifications);
  const [savedLanguages, setSavedLanguages]           = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedSoftware, setSavedSoftware]             = useState([]);
  const [savedReferences, setSavedReferences]         = useState([]);
  const [savedVolunteering, setSavedVolunteering]     = useState([]);
  const [savedInterests, setSavedInterests]           = useState([]);
  const [savedWebsites, setSavedWebsites]             = useState([]);
  const [customSections, setCustomSections]           = useState({});

  const [sectionOrder, setSectionOrder]   = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ─── Font helpers ─────────────────────────────────────────────────────────────
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (extra = {}) => ({ ...globalFontStyle, ...safeText, ...extra });

  const sectionTitleStyle = {
    color: themeColor,
    fontWeight: "bold",
    fontSize: headingSize,
    marginTop: `${sectionSpacing}px`,
    marginBottom: "8px",
    fontFamily: font,
    fontStyle: fontStyle,
    ...safeText,
  };

  const paragraphStyle = {
    marginTop: `${paragraphSpacing}px`,
    textIndent: `${paragraphIndent}px`,
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    ...safeText,
  };

  // ─── Data Loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setThemeColor(color || theme);

    try {
      const sums = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (sums.length > 0) setSavedSummary(sums[sums.length - 1]);

      const se = localStorage.getItem("educationEntries"); if (se) setEducationEntries(JSON.parse(se));
      const ss = localStorage.getItem("skills");           if (ss) setSavedSkills(JSON.parse(ss));

      setWorkExperiences(JSON.parse(localStorage.getItem("workExperiences")     || "[]") || data.workExperiences);
      setSavedWebsites(JSON.parse(localStorage.getItem("websites")              || "[]"));
      setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList") || "[]") || data.certifications);
      setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")        || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList") || "[]"));
      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")  || "[]"));
      setSavedSoftware(JSON.parse(localStorage.getItem("softwareSkills")        || "[]"));
      setSavedReferences(JSON.parse(localStorage.getItem("referencesList")      || "[]"));
      setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")      || "[]"));
      setSavedInterests(JSON.parse(localStorage.getItem("interests")            || "[]"));

      // Custom sections
      const userId = localStorage.getItem("userId");
      let sectionsData = {};
      if (userId) {
        const current = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`) || "[]");
        current.forEach(name => {
          try {
            const raw = localStorage.getItem(`custom_${userId}_${name}`);
            if (raw) {
              const parsed = JSON.parse(raw);
              const valid = (parsed.items || []).filter(i => i && i.trim() !== '');
              if (valid.length > 0) sectionsData[name] = valid;
            }
          } catch (e) {}
        });
      }
      setCustomSections(sectionsData);

      // Section order
      const saved = localStorage.getItem("resumeMatthewSectionOrder");
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved).filter(s => !s.startsWith('custom_'));
          setSectionOrder([...parsed, ...customIds]);
        } catch { setSectionOrder([...defaultSectionOrder, ...customIds]); }
      } else {
        setSectionOrder([...defaultSectionOrder, ...customIds]);
      }
    } catch (e) {}
  }, [color, theme]);

  useEffect(() => {
    if (sectionOrder.length > 0)
      localStorage.setItem("resumeMatthewSectionOrder", JSON.stringify(sectionOrder));
  }, [sectionOrder]);

  const formatDate = (month, year) => (!month || !year) ? "" : `${month} ${year}`;

  // ─── Drag & Drop ──────────────────────────────────────────────────────────────
  const handleDragStart = (e, i) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index: i, sectionId: sectionOrder[i] }));
    e.currentTarget.style.opacity = '0.5';
  };
  const handleDragOver  = (e, i) => { e.preventDefault(); setDragOverIndex(i); };
  const handleDragLeave = (e)    => { e.preventDefault(); setDragOverIndex(null); };
  const handleDragEnd   = (e)    => { setDragOverIndex(null); e.currentTarget.style.opacity = '1'; };

  const handleDrop = (e, ti) => {
    e.preventDefault();
    const { index: si } = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (si !== ti) {
      const arr = [...sectionOrder];
      const [m] = arr.splice(si, 1);
      arr.splice(ti, 0, m);
      setSectionOrder(arr);
    }
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetSectionOrder = () => setSectionOrder(defaultSectionOrder);

  const getSectionTitle = (id) => {
    const titles = {
      summary: 'Summary', skills: 'Skill Highlights', experience: 'Experience',
      education: 'Education', certifications: 'Certifications', languages: 'Languages',
      accomplishments: 'Accomplishments', additionalInfo: 'Additional Information',
      softwareSkills: 'Software Skills', volunteering: 'Volunteering',
      interests: 'Interests', websites: 'Websites & Profiles', references: 'References'
    };
    if (id.startsWith('custom_')) {
      const n = id.replace('custom_', '').replace(/_/g, ' ');
      return n.charAt(0).toUpperCase() + n.slice(1);
    }
    return titles[id] || id;
  };

  const renderDragPanel = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        Resume Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>({sectionOrder.length} sections)</Typography>
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Drag and drop to reorder sections.
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sectionOrder.map((sectionId, index) => (
          <Paper key={sectionId} draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{ p:2, mb:1, display:'flex', alignItems:'center', backgroundColor: dragOverIndex===index ? '#f0f0f0':'white', boxShadow: dragOverIndex===index ? 2:1, cursor:'grab', '&:active':{cursor:'grabbing'}, transition:'all 0.2s ease' }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml:1, flexGrow:1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index+1}</Typography>
          </Paper>
        ))}
      </Box>
      <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt:1 }}>Reset Section Order</Button>
    </Box>
  );

  // ─── Section Renderers ────────────────────────────────────────────────────────
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'summary':
        return savedSummary ? (
          <Box sx={{ mt:`${sectionSpacing}px` }} className="resume-section" id="summary-section">
            <Typography sx={sectionTitleStyle}>Summary</Typography>
            <Typography variant="body2" sx={paragraphStyle}>{savedSummary}</Typography>
          </Box>
        ) : null;

      case 'skills':
        return savedSkills.length > 0 ? (
          <Box className="resume-section" id="skills-section">
            <Typography sx={sectionTitleStyle}>Skill Highlights</Typography>
            <Grid container spacing={1}>
              {savedSkills.map((skill, i) => (
                <Grid item xs={6} key={i}>
                  <Typography variant="body2" sx={getTextStyle()}>
                    • {skill.name}{skill.rating ? ` (${skill.rating}%)` : ''}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null;

      case 'experience':
        return workExperiences.length > 0 ? (
          <Box className="resume-section" id="experience-section">
            <Typography sx={sectionTitleStyle}>Experience</Typography>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mt:`${paragraphSpacing}px` }}>
                {/* ✅ FIX: flexWrap so date never overflows */}
                <Box sx={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:0.5 }}>
                  <Typography fontWeight="bold" variant="body2" sx={{ ...getTextStyle(), flex:'1 1 auto' }}>
                    {work.jobTitle} – {work.employer}
                  </Typography>
                  <Typography variant="body2" sx={{ ...getTextStyle(), fontStyle:'italic', flexShrink:0 }}>
                    {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontStyle:"italic", ...globalFontStyle, ...safeText }}>
                  {work.location}
                </Typography>
                {work.description && (
                  <Box
                    sx={{ ...globalFontStyle, ...safeText,
                      "& strong":{ fontWeight:"bold" }, "& em":{ fontStyle:"italic" }, "& u":{ textDecoration:"underline" } }}
                    dangerouslySetInnerHTML={{ __html: work.description
                      .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family:${font};font-size:${fontSize};font-style:${fontStyle}">$1</strong>`)
                      .replace(/\*(.*?)\*/g, `<em style="font-family:${font};font-size:${fontSize}">$1</em>`)
                      .replace(/<u>(.*?)<\/u>/g, `<u style="font-family:${font};font-size:${fontSize};font-style:${fontStyle}">$1</u>`)
                      .replace(/\n/g, "<br/>")
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <Box className="resume-section" id="education-section">
            <Typography sx={sectionTitleStyle}>Education</Typography>
            {educationEntries.map((edu, i) => {
              const institutionName = edu.educationLevel === "school" ? edu.schoolName  : edu.collegeName;
              const degreeName      = edu.educationLevel === "school" ? edu.standard    : edu.degreeName;
              const fieldOfStudy    = edu.educationLevel === "school" ? edu.stream      : edu.specialization;
              const location        = edu.educationLevel === "school" ? edu.schoolLocation : edu.collegeLocation;
              return (
                <Box key={i} sx={{ mt:`${paragraphSpacing}px` }}>
                  <Typography variant="body2" fontWeight="bold" sx={getTextStyle()}>
                    {degreeName}{fieldOfStudy && ` - ${fieldOfStudy}`}
                  </Typography>
                  <Typography variant="body2" sx={getTextStyle()}>
                    {institutionName}{location && `, ${location}`}{edu.district && `, ${edu.district}`}
                  </Typography>
                  {(edu.percentage || edu.cgpa || edu.grade) && (
                    <Typography variant="body2" sx={getTextStyle()}>
                      {edu.percentage && `Percentage: ${edu.percentage}%`}
                      {edu.cgpa && `${edu.percentage ? ' | ' : ''}CGPA: ${edu.cgpa}`}
                      {edu.grade && `${(edu.percentage || edu.cgpa) ? ' | ' : ''}Grade: ${edu.grade}`}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontStyle:"italic", ...globalFontStyle, ...safeText }}>
                    {formatDate(edu.gradMonth, edu.gradYear)}
                  </Typography>
                  {edu.additionalCoursework && (
                    <Typography variant="body2" sx={getTextStyle()}>{edu.additionalCoursework}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box className="resume-section" id="certifications-section">
            <Typography sx={sectionTitleStyle}>Certifications</Typography>
            {savedCertifications.map((cert, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>
                {cert.name} – {cert.provider} ({cert.year})
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box className="resume-section" id="languages-section">
            <Typography sx={sectionTitleStyle}>Languages</Typography>
            {savedLanguages.map((lang, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>
                {lang.name}{lang.level ? ` – ${lang.level}` : ""}
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box className="resume-section" id="accomplishments-section">
            <Typography sx={sectionTitleStyle}>Accomplishments</Typography>
            {savedAccomplishments.map((acc, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>• {acc}</Typography>
            ))}
          </Box>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box className="resume-section" id="additional-info-section">
            <Typography sx={sectionTitleStyle}>Additional Information</Typography>
            {savedAdditionalInfo.map((info, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>• {info}</Typography>
            ))}
          </Box>
        ) : null;

      case 'softwareSkills':
        return savedSoftware.length > 0 ? (
          <Box className="resume-section" id="software-skills-section">
            <Typography sx={sectionTitleStyle}>Software Skills</Typography>
            {savedSoftware.map((s, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>{s.name} – {s.level}%</Typography>
            ))}
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box className="resume-section" id="volunteering-section">
            <Typography sx={sectionTitleStyle}>Volunteering</Typography>
            {savedVolunteering.map((vol, i) => (
              <Box key={i} sx={{ mb:2 }}>
                <Typography variant="body2" fontWeight="bold" sx={getTextStyle()}>{vol.subtopic}</Typography>
                <Typography variant="body2" sx={{ fontStyle:"italic", ...globalFontStyle, ...safeText }}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography variant="body2" sx={paragraphStyle}>{vol.content}</Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box className="resume-section" id="interests-section">
            <Typography sx={sectionTitleStyle}>Interests</Typography>
            {savedInterests.map((interest, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>• {interest}</Typography>
            ))}
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box className="resume-section" id="websites-section">
            <Typography sx={sectionTitleStyle}>Websites & Profiles</Typography>
            {savedWebsites.map((site, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>
                <a href={site.url.startsWith('http') ? site.url : `https://${site.url}`} target="_blank" rel="noopener noreferrer"
                  style={{ color:'inherit', textDecoration:'none', fontFamily:font, fontSize, wordBreak:'break-all' }}>
                  {site.name || site.url}
                </a>
                {site.addToHeader ? " (Shown in Header)" : ""}
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box className="resume-section" id="references-section">
            <Typography sx={sectionTitleStyle}>References</Typography>
            {savedReferences.map((ref, i) => (
              <Typography key={i} variant="body2" sx={paragraphStyle}>• {ref}</Typography>
            ))}
          </Box>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = (customSections[customSectionName] || []).filter(item => item && item.trim() !== '');
          if (items.length > 0) {
            return (
              <Box className="resume-section" id={`custom-${customSectionName}-section`}>
                <Typography sx={sectionTitleStyle}>
                  {customSectionName.replace(/_/g, ' ').toUpperCase()}
                </Typography>
                {items.map((item, i) => (
                  <Typography key={i} variant="body2" sx={paragraphStyle}>• {item}</Typography>
                ))}
              </Box>
            );
          }
        }
        return null;
    }
  };

  // ─── Header renderer ──────────────────────────────────────────────────────────
  const renderHeader = () => (
    <Box
      className="resume-header"
      sx={{
        bgcolor: themeColor,
        color: "#fff",
        p: 3,
        textAlign: "center",
        overflow: 'hidden',          // ✅ FIX: header content contained
        boxSizing: 'border-box',
        ...globalFontStyle,
        "@media print": { bgcolor: themeColor, color:"#fff", WebkitPrintColorAdjust:"exact", printColorAdjust:"exact" },
      }}
    >
      {data.phone && (
        <Typography variant="body1" sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1, flexWrap:'wrap', ...getTextStyle() }}>
          <PhoneIcon sx={{ fontSize:16, flexShrink:0 }} /> {data.phone}
        </Typography>
      )}
      {data.email && (
        <Typography variant="body1" sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1, flexWrap:'wrap', ...getTextStyle() }}>
          <EmailIcon sx={{ fontSize:16, flexShrink:0 }} />
          <span style={{ wordBreak:'break-all' }}>{data.email}</span>
        </Typography>
      )}
      {data.website && (
        <Typography variant="body1" sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1, flexWrap:'wrap', ...getTextStyle() }}>
          <LanguageIcon sx={{ fontSize:16, flexShrink:0 }} />
          <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`} target="_blank" rel="noopener noreferrer"
            style={{ color:'#fff', textDecoration:'none', fontFamily:font, fontSize, wordBreak:'break-all' }}>
            {data.website.replace(/^https?:\/\/(www\.)?/, '')}
          </a>
        </Typography>
      )}
      {data.linkedIn && (
        <Typography variant="body1" sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1, flexWrap:'wrap', ...getTextStyle() }}>
          <LinkedInIcon sx={{ fontSize:16, color:'#fff', flexShrink:0 }} />
          <a href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`} target="_blank" rel="noopener noreferrer"
            style={{ color:'#fff', textDecoration:'none', fontFamily:font, fontSize, wordBreak:'break-all' }}>
            {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
          </a>
        </Typography>
      )}
      {data.city && (
        <Typography variant="body1" sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1, flexWrap:'wrap', ...getTextStyle() }}>
          <LocationOnIcon sx={{ fontSize:16, flexShrink:0 }} /> {data.city}
        </Typography>
      )}
      {data.drivingLicence && (
        <Typography variant="body1" sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1, flexWrap:'wrap', ...getTextStyle() }}>
          <DriveEtaIcon sx={{ fontSize:16, flexShrink:0 }} /> DL: {data.drivingLicence}
        </Typography>
      )}
      <Typography variant="h4" fontWeight="bold" mt={1} sx={getTextStyle({ fontSize:"2rem", wordBreak:'break-word' })}>
        {data.firstName.toUpperCase()} {data.lastName.toUpperCase()}
      </Typography>
      <Typography variant="h6" mt={1} sx={getTextStyle({ fontSize:"1.2rem", wordBreak:'break-word' })}>
        {data.currentPosition}
      </Typography>
    </Box>
  );

  // ─── Pagination ───────────────────────────────────────────────────────────────
  const splitContentIntoPages = useCallback(() => {
    if (!resumeRef.current) return;

    const headerEl = resumeRef.current.querySelector('.resume-header');
    const sections = Array.from(resumeRef.current.querySelectorAll('.resume-section'));
    if (!headerEl) return;

    const headerHeight = headerEl.offsetHeight;
    // ✅ FIX: use numeric px height
    const pageHeightInPx        = parseInt(page.minHeight.replace('px',''), 10);
    const availableFirstPage    = pageHeightInPx - headerHeight - (2 * topBottomMargin);
    const availableSubsequent   = pageHeightInPx - (2 * topBottomMargin);

    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const sectionHeight = section.offsetHeight;
      const limit = newPages.length === 0 ? availableFirstPage : availableSubsequent;

      if (currentHeight + sectionHeight > limit) {
        if (currentPageSections.length > 0) newPages.push(currentPageSections);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
    });

    if (currentPageSections.length > 0) newPages.push(currentPageSections);
    if (newPages.length === 0) newPages.push([]);
    setPages(newPages);
  }, [page.minHeight, topBottomMargin, workExperiences, educationEntries, savedSkills, savedSummary,
      savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences,
      savedSoftware, savedVolunteering, savedInterests, savedWebsites, customSections, sectionOrder,
      font, fontSize, fontStyle, lineSpacing, sectionSpacing, paragraphSpacing]);

  useEffect(() => {
    const id = setTimeout(() => splitContentIntoPages(), 500);
    return () => clearTimeout(id);
  }, [splitContentIntoPages]);

  // ─── Hidden measurement box ───────────────────────────────────────────────────
  // ✅ FIX: must match actual rendered page width with padding
  const renderContentForMeasurement = () => (
    <Box ref={resumeRef} sx={{
      visibility:'hidden', position:'absolute', left:'-9999px', top:0,
      width: page.width,
      boxSizing:'border-box',
      overflow:'hidden',
      ...globalFontStyle
    }}>
      {renderHeader()}
      <Box sx={{ p:`0 ${sideMargins}px`, boxSizing:'border-box' }}>
        {sectionOrder.map(sectionId => (
          <React.Fragment key={sectionId}>{renderSectionContent(sectionId)}</React.Fragment>
        ))}
      </Box>
    </Box>
  );

  // ✅ A4 page styles — strictly bounded
  const pageBoxStyles = {
    width: page.width,
    minHeight: page.minHeight,
    mx: "auto",
    bgcolor: "#fff",
    color: "#000",
    fontFamily: font,
    fontSize,
    fontStyle,
    lineHeight: `${lineSpacing}px`,
    overflow: 'hidden',          // ✅ KEY: nothing escapes A4
    boxSizing: 'border-box',
    pageBreakAfter: "always",
    "@media print": {
      width: page.width,
      minHeight: page.minHeight,
      boxShadow: "none",
      m: 0,
      overflow: 'hidden',
    },
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width:"100%", position:"relative" }}>
      {!exporting && (
        <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', mb:2, gap:2, '@media print':{ display:'none' } }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p-1))} disabled={currentPage===0} color="primary"><ChevronLeft /></IconButton>
          <Typography sx={{ mx:2 }}>Page {currentPage+1} of {Math.max(1, pages.length)}</Typography>
          <IconButton onClick={() => setCurrentPage(p => Math.min(pages.length-1, p+1))} disabled={currentPage===Math.max(0,pages.length-1)} color="primary"><ChevronRight /></IconButton>
          {enableDragDrop && (
            <Box sx={{ display:'flex', gap:2, alignItems:'center', ml:2 }}>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary">Arrange Sections</Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small">Reset Section Order</Button>
            </Box>
          )}
        </Box>
      )}

      {renderContentForMeasurement()}

      <Box id="resume-container">
        {pages.length > 0 ? pages.map((pageContent, index) => (
          <Box key={index} className="resume-page" sx={{
            ...pageBoxStyles,
            boxShadow: !exporting && index===currentPage ? 3 : 0,
            display: exporting ? 'block' : (index===currentPage ? 'block' : 'none'),
            '@media print': { display:'block' }
          }}>
            {/* Header only on first page */}
            {index === 0 && renderHeader()}

            {/* ✅ Content area — padded, overflow hidden */}
            <Box sx={{
              p: `${topBottomMargin}px ${sideMargins}px`,
              boxSizing: 'border-box',
              overflow: 'hidden',
              ...globalFontStyle,
            }}>
              <div
                style={{ wordBreak:'break-word', overflowWrap:'break-word', fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px` }}
                dangerouslySetInnerHTML={{ __html: pageContent.join('') }}
              />
            </Box>
          </Box>
        )) : (
          // Empty fallback
          <Box className="resume-page" sx={{ ...pageBoxStyles, boxShadow:3, display:'block' }}>
            {renderHeader()}
            <Box sx={{ p:`${topBottomMargin}px ${sideMargins}px`, boxSizing:'border-box', overflow:'hidden' }}>
              {sectionOrder.map(id => <React.Fragment key={id}>{renderSectionContent(id)}</React.Fragment>)}
            </Box>
          </Box>
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center"><Reorder sx={{ mr:1 }} />Arrange Resume Sections</Box></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb:3 }}>
            Drag and drop to reorder sections. Sections will appear in the order shown below.
          </Typography>
          {renderDragPanel()}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset Order</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResumeMatthew;